import {
  FormControl,
  InputBase,
  InputLabel,
  InputLabelProps,
  MenuItem,
  Select,
  SelectChangeEvent,
  SelectProps,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { isNotNullOrUndefined, isNullOrUndefined } from '@vy/utils';
import { useCallback } from 'react';

type BaseItem = { key: React.Key };

type VySelectProps<SelectItem extends BaseItem> = {
  label?: React.ReactNode;
  options: SelectItem[];
  onSelectionChange: (item?: SelectItem) => void;
  render: (item: SelectItem, renderContext: 'selected' | 'menuItem') => React.ReactNode;
  keyExtractor: (item: SelectItem) => string;
  shouldDisableOption?: (item: SelectItem) => boolean;
  placeholder?: string;
  selectedOption?: SelectItem;
  labelProps?: InputLabelProps;
  selectProps?: SelectProps;
  fullWidth?: boolean;
  error?: boolean;
};

export const VySelect = <SelectItem extends BaseItem>({
  label,
  options,
  placeholder,
  onSelectionChange,
  render,
  keyExtractor,
  selectedOption,
  labelProps,
  selectProps,
  fullWidth = false,
  error,
  shouldDisableOption,
}: VySelectProps<SelectItem>) => {
  const { sx: labelSx, ...restLabelProps } = labelProps ?? {};
  const { MenuProps, sx: selectSx, ...restSelectProps } = selectProps ?? {};
  const { sx: menuSx, ...restMenuProps } = MenuProps ?? {};

  const handleChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      const value = event.target.value;
      const selectedItem = options.find((option) => option.key === value);

      onSelectionChange(selectedItem);
    },
    [options, onSelectionChange],
  );

  const handleRenderValue = useCallback(
    (value: unknown) => {
      if (isNullOrUndefined(value) && isNotNullOrUndefined(placeholder)) {
        return (
          <Typography sx={{
            color: "text.secondary"
          }}>{placeholder}</Typography>
        );
      }

      const selectedItem = options.find((option) => option.key === value);

      return selectedItem ? render(selectedItem, 'selected') : null;
    },
    [placeholder, options, render],
  );

  return (
    <FormControl fullWidth={fullWidth} error={error}>
      {isNotNullOrUndefined(label) && (
        <StyledInputLabel shrink sx={labelSx} {...restLabelProps}>
          {label}
        </StyledInputLabel>
      )}
      <StyledSelect
        MenuProps={{
          sx: {
            maxHeight: '300px',
            ...menuSx,
          },
          ...restMenuProps,
        }}
        sx={selectSx}
        value={selectedOption?.key}
        displayEmpty
        renderValue={handleRenderValue}
        input={<InputBase />}
        onChange={handleChange}
        {...restSelectProps}
      >
        {options.map((option) => (
          <MenuItem key={keyExtractor(option)} value={option.key} disabled={shouldDisableOption?.(option)}>
            {render(option, 'menuItem')}
          </MenuItem>
        ))}
      </StyledSelect>
    </FormControl>
  );
};

const StyledInputLabel = styled(InputLabel)({
  position: 'static',
  transform: 'none',
  marginBottom: '8px',
  color: '#7E8796',
  fontSize: '12px',
  fontWeight: 400,
});

const StyledSelect = styled(Select)({
  backgroundColor: '#0D1B270A',
  borderRadius: '12px',
  border: 'none',
  height: '48px',
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    padding: '0 8px',
  },
});
